<template>
    <div>
        <v-server-table :name="tableName" :ref="tableName" url="/equipment/list" :columns="columns" :options="options">

            <div slot="afterFilterWrapper" class="ml-md-2 ml-0 mt-md-0 mt-2 mb-md-0 mb-3 float-md-left">
                <div class="clearfix d-md-none d-block"></div>

                <div v-if="allowFilterSaving" class="float-left mr-1">
                    <user-filters ref="equipmentSavedFilters" assetType="Equipment" :filterKey="filterKey" @saving="setUserFilterData" @show="setUserFilterData"></user-filters>
                </div>

                <div class="float-left">
                    <table-filters-2 ref="equipmentTableFilters"
                        filterUrl="/equipment/filters"
                        v-bind:filters="filters"
                        :emptyFilters="filterDefs"
                        :filterKey="filterKey"
                        @filtersChanged="refreshTableData"
                        @filtersCleared="filtersCleared"
                        v-show="!dashboardFilters">
                        <template slot-scope="slotProps">
                            <div class="d-inline-block">
                                <multi-select v-model="slotProps.filters['equipment.equipment_type_id']" :btnLabel="(values) => ('Equipment Types ('+values.length+')')" :selectOptions="slotProps.filterData.equipmentTypes" :options="{multi: true}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="slotProps.filters['equipment.division_id']" :btnLabel="(values) => ('Divisions ('+values.length+')')" :selectOptions="slotProps.filterData.divisions" :options="{multi: true}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="slotProps.filters['equipment.current_tracking_status']" :btnLabel="(values) => ('Statuses ('+values.length+')')" :selectOptions="slotProps.filterData.tracking_statuses" :options="{multi: true}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <date-range-selector buttonTitle="Issue Date" v-model="slotProps.filters['equipment.issue_date']" />
                            </div>
                            <div class="d-inline-block">
                                <date-range-selector buttonTitle="Calibrated Date" v-model="slotProps.filters['equipment.calibrate_date']" />
                            </div>
                            <div class="d-inline-block">
                                <date-range-selector buttonTitle="Return Date" v-model="slotProps.filters['equipment.return_date']" />
                            </div>
                            <div class="d-inline-block">
                                <date-range-selector buttonTitle="Purchase Date" v-model="slotProps.filters['equipment.purchase_date']" />
                            </div>
                            <hr/>

                            <div class="d-inline-block">
                                <b-form-select v-model="slotProps.filters['equipment.due_for_calibration']" size="sm" :options="[{value: null, text: '-- Show All Calibration Needs --'}, {value: 1, text: 'Due for Calibration'}]" />
                            </div>
                            <div class="d-inline-block" style="width: 300px;">
                                <b-form-select v-model="slotProps.filters['equipment.has_corrective_actions']" size="sm" :options="[{value: null, text: '-- Corrective Actions - Show All --'}, {value: 1, text: 'Has Open Corrective Actions'}]" />
                            </div>
                            <hr/>

                            <div class="d-inline-block" style="width: 300px;">
                                <jobsite-select :selectedValues="slotProps.filters['equipment.jobsite_id']" :forFilters="true" v-on:vselected="((data) => { slotProps.filters['equipment.jobsite_id'] = data || {};  })" />
                            </div>
                            <div class="d-inline-block" style="width: 300px;" v-if="$root.hasPermission(['employees.view'])">
                                <employee-select :forFilters="true" :selectedValues="slotProps.filters['equipment.employee_id']" v-on:vselected="((data) => { slotProps.filters['equipment.employee_id'] = data || {};  })"></employee-select>
                            </div>
                        </template>
                    </table-filters-2>
                </div>

                <button type="button" class="btn btn-outline-success ml-2" v-on:click="exportDataTable()" :disabled="exportingTable">
                    <template v-if="!exportingTable"><i class="fa fa-file-excel-o"></i> Export</template>
                    <template v-else><i class="fa fa-circle-o-notch fa-spin"></i> Exporting</template>
                </button>

                <router-link :to="`/employees/equipment/${assetId}`" v-if="assetType === 'Employee'" class="btn btn-warning">
                    Assign/Edit Equipment
                </router-link>

                <div class="clearfix d-md-none d-block"></div>
            </div>

            <router-link slot="equipment_id" slot-scope="props" :to="`/equipment/view/${props.row.equipment_id}`" title="View Equipment" class="btn btn-sm btn-secondary">
                View
            </router-link>

            <template slot="current_tracking_status" slot-scope="props">
                <template v-if="(props.row.current_tracking_status == 'Checked In' || props.row.current_tracking_status == null) && $root.hasPermission(['equipment.manage', 'equipment.create'])">
                    <router-link :to="`/equipment/tracking/${props.row.equipment_id}/edit`">
                        <span class="badge" :class="status_color(props.row.current_tracking_status || 'Checked In')"><i class="fa fa-edit"></i> {{ props.row.current_tracking_status || 'Checked In' }}</span>
                    </router-link>
                </template>
                <template v-else-if="(props.row.current_tracking_status != 'Checked In' && props.row.current_tracking_status != null) && ($root.hasPermission(['equipment.manage', 'equipment.create']) || props.row.user_id == $store.state.user.user_id)">
                    <router-link :to="`/equipment/tracking/${props.row.equipment_id}/edit`">
                        <span class="badge" :class="status_color(props.row.current_tracking_status)"><i class="fa fa-edit"></i>{{ props.row.current_tracking_status }}</span>
                    </router-link>
                </template>
                <template v-else>
                    <span class="badge" :class="status_color(props.row.current_tracking_status)">{{ props.row.current_tracking_status || 'Checked In' }}</span>
                </template>
            </template>

            <template slot="equipment_type_name" slot-scope="props">
                {{ props.row.equipment_type_name }}
            </template>

            <template slot="equipment_name" slot-scope="props">
                <router-link :to="`/equipment/view/${props.row.equipment_id}`" title="View Equipment" v-if="props.row.equipment_name">
                    {{ props.row.equipment_name }}
                </router-link>
            </template>

            <template slot="serial_number" slot-scope="props">
                <router-link :to="`/equipment/view/${props.row.equipment_id}`" title="View Equipment">
                    {{ props.row.serial_number }}
                </router-link>
            </template>

            <template slot="issue_date" slot-scope="props">
                <template v-if="props.row.issue_date">{{ props.row.issue_date | moment("MM/DD/YYYY") }}</template>
            </template>

            <template slot="next_maintenance_date" slot-scope="props">
                <span v-if="props.row.next_maintenance_date" :class="{'badge badge-danger': $root.isDateOverdue(props.row.next_maintenance_date)}">
                    {{ props.row.next_maintenance_date | moment("MM/DD/YYYY") }}
                </span>
            </template>

            <template slot="calibrate_date" slot-scope="props">
                <template v-if="props.row.calibrate_date">
                    <span v-if="props.row.days_calibration_valid_for"
                        :class="{'badge badge-danger': $root.isDateOverdue(props.row.calibrate_date, props.row.days_calibration_valid_for)}"
                        :title="'Calibration is Valid for '+props.row.days_calibration_valid_for+' Days'">
                        {{ props.row.calibrate_date | moment("MM/DD/YYYY") }}
                    </span>
                    <span v-else>
                        {{ props.row.calibrate_date | moment("MM/DD/YYYY") }}
                    </span>
                </template>
            </template>

            <template slot="return_date" slot-scope="props">
                <template v-if="props.row.return_date">{{ props.row.return_date | moment("MM/DD/YYYY") }}</template>
            </template>

            <template slot="purchase_date" slot-scope="props">
                <template v-if="props.row.purchase_date">{{ props.row.purchase_date | moment("MM/DD/YYYY") }}</template>
            </template>

            <template slot="EmployeeLastName" slot-scope="props">
                <router-link :to="`/employees/view/${props.row.employee_id}`" v-if="props.row.employee_id">
                    {{ props.row.EmployeeFirstName + ' ' + props.row.EmployeeLastName }}
                </router-link>
            </template>

            <template slot="JobsiteName" slot-scope="props">
                <router-link :to="`/jobsites/view/${props.row.jobsite_id}`" v-if="props.row.jobsite_id">
                    {{ props.row.JobsiteNumber }} - {{ props.row.JobsiteName }}
                </router-link>
            </template>

            <template slot="DivisionName" slot-scope="props">
                <template v-if="props.row.DivisionName">
                    <template v-if="props.row.division_code">{{ props.row.division_code }} -</template> {{ props.row.DivisionName }}
                </template>
            </template>

            <template slot="child_row" slot-scope="props">
                <div class="mb-2">
                    <div class="text-muted">Equipment Notes:</div>
                    {{ props.row.equipment_notes }}
                </div>
            </template>

        </v-server-table>

        <template v-if="!dashboardFilters">
            <hr>
            <span v-if="!getDeleted">To view inactive equipment, click <a href="javascript:void(0);" @click="getDeleted = true">here</a>.</span>
            <span v-else>To go back to viewing active equipment, click <a href="javascript:void(0);" @click="getDeleted = false">here</a>.</span>
        </template>
    </div>
</template>

<script>
    import filterExtractor from '@/helpers/TableFilterExtractor';
    import customVTColDropdown from '@/components/vue-tables-2/CustomColumnDropdown';

    export default {
        props: {
            assetType: {
                type: String,
                default: null,
            },

            assetId: {
                type: Number,
                default: null,
            },

            allowFilterSaving: {
                type: Boolean,
                default: false,
            },

            filterId: {
                type: Number,
                default: null,
            },

            dashboardFilters: {
                type: Object,
                default(){
                    return null;
                },
            },

            tableConfig: {
                type: Object,
                default(){
                    return null;
                },
            },
        },

        data() {
            return {
                exportingTable: false,
                tableName: "equipmentTable",
                columns: [
                    'equipment_id',
                    'current_tracking_status',
                    'equipment_name',
                    'equipment_type_name',
                    'model_number',
                    'serial_number',
                    'issue_date',
                    'next_maintenance_date',
                    'calibrate_date',
                    'return_date',
                    'purchase_date',
                    'EmployeeLastName',
                    'JobsiteName',
                    'DivisionName'
                ],

                getDeleted: false,

                filterDefs: {
                    'equipment.equipment_type_id': [],
                    'equipment.jobsite_id' : {},
                    'equipment.division_id': [],
                    'equipment.issue_date' : '',
                    'equipment.next_maintenance_date' : '',
                    'equipment.calibrate_date' : '',
                    'equipment.return_date' : '',
                    'equipment.purchase_date' : '',
                    'equipment.current_tracking_status' : [],
                    'equipment.employee_id': {},
                    'equipment.has_corrective_actions': null,
                    'equipment.due_for_calibration': null,
                },

                filterKey: 'equipmentFilters',
            }
        },

        watch: {
            //Watcher for errors
            getDeleted(newValue, oldValue){
                //Set it
                this.$refs.equipmentTable.refresh();
            },

            appliedFilter(newVal, oldVal){
                //Call the helper
                this.checkForAppliedFilter();
            },
        },

        computed: {
            filters(){
                //Check State
                if(this.$store.state.tableFilters.filters[this.filterKey])
                {
                    //Send it
                    return this.$store.state.tableFilters.filters[this.filterKey];
                }

                //Return Definition
                return JSON.parse(JSON.stringify(this.filterDefs));
            },

            appliedFilter(){
                //Send it
                return this.$store.state.appliedFilters[this.filterKey] ? this.$store.state.appliedFilters[this.filterKey] : null;
            },

            options(){
                return {
                    headings: {
                        equipment_id: 'Actions',
                        current_tracking_status: 'Status',
                        equipment_type_name: 'Type',
                        equipment_name: 'Name',
                        model_number: 'Model#',
                        serial_number: 'Serial#',
                        issue_date: 'Issued',
                        next_maintenance_date: 'Next Maintenance',
                        calibrate_date: 'Last Calibration',
                        return_date: 'Return',
                        purchase_date: 'Purchased',
                        EmployeeLastName: 'Issued To',
                        JobsiteName: 'Jobsite',
                        DivisionName: 'Division',
                    },
                    saveState: this.allowFilterSaving,
                    columnsDropdown: customVTColDropdown,
                    visibleColumns: (this.allowFilterSaving ? this.$root.getVisibleColumnsFromState(this.tableName) : false),
                    sendInitialRequest: (this.filterId ? false : true),
                    orderBy: {
                        ascending: false,
                        column: 'issue_date',
                    },
                    responseAdapter: (response) => {
                        //Emit the count
                        this.$emit('equipmentListCount', response.data.count);

                        //Send it
                        return response.data;
                    },
                    requestAdapter: (data) => {
                        //Set the filters
                        data.filters = (this.dashboardFilters ? filterExtractor.extractFilters(this.dashboardFilters) : filterExtractor.extractFilters(this.filters));

                        //INclude the asset
                        data.assetType = this.assetType;
                        data.assetId = this.assetId;

                        //Send it
                        return data;
                    },
                    uniqueKey: 'equipment_id',
                };
            },
        },

        methods: {
            status_color(status) {
                let color = 'badge-success';

                switch (status)
                {
                    case 'Checked In':
                        color = 'badge-success';
                        break;
                    case 'Issued':
                        color = 'badge-warning';
                        break;
                    case 'On Hold':
                        color = 'badge-danger';
                        break;
                    case 'Archived':
                        color = 'badge-dark';
                        break;
                }

                return color;
            },

            exportDataTable(){
                //Loading indicator
                this.exportingTable = true;

                //Build the payload
                var payload = {
                    filters: filterExtractor.extractFilters(this.filters),
                    query: (this.$refs.equipmentTable.getRequestParams()).query,
                    orderBy: this.$refs.equipmentTable.options.orderBy.column,
                    ascending: this.$refs.equipmentTable.options.orderBy.ascending,
                    table: 'equipmentTable',
                    columns: this.$refs.equipmentTable.columns,
                    visible_columns: this.$root.getVisibleColumnsFromState(this.tableName),
                };

                //Send it
                this.$http.get('/equipment/list?export=true', payload)
                    .then(response => {
                        //Open the new window
                        window.open(response.data);
                    })
                    .catch(err => {
                        //Alert the user
                        this.$toastr.error(err.response.data, 'Failed to Export Equipment List');
                    })
                    .then(response => {
                        //reset
                        this.exportingTable = false;
                    });
            },

            refreshTableData(filters) {
                //Set the data
                this.$refs.equipmentTable.setPage(1);
                //this.$refs.equipmentTable.getData();
            },

            filtersCleared(){
                //Reset
                this.refreshTableData();

                //Reset the Columns
                this.$refs.equipmentTableFilters.resetColVis(this.$refs.equipmentTable);
            },

            setUserFilterData(){
                //Call the Helper
                this.$refs.equipmentSavedFilters.setFilterParams(this.$refs.equipmentTable);
            },

            checkForAppliedFilter(){
                //Check if
                if(!this.allowFilterSaving || !this.appliedFilter)
                {
                    return;
                }

                //Call the helper
                this.$refs.equipmentTableFilters.setColVis(this.$refs.equipmentTable);
            },
        },

        mounted(){
            //Check for Staring Filters
            if(!this.dashboardFilters)
            {
                //Set it
                //this.$refs.inspTableFilters.setFilters(this.dashboardFilters);

                //Check for a saved filter
                this.checkForAppliedFilter();

                //Check for Filter ID
                if(this.filterId)
                {
                    this.$refs.equipmentSavedFilters.setSavedFilter(this.filterId);
                }
            }

            //Check for Table Config
            if(this.tableConfig)
            {
                //Set it
                this.$refs.equipmentTableFilters.setColVisFromSavedFilter(this.$refs.equipmentTable, this.tableConfig);
            }
        }
    }
</script>
